import compact from "lodash/compact";
import keys from "lodash/keys";
import { CvExportsButton } from "PFApp/components/cv_exports_button";
import {
  FiltersV2Button,
  FiltersV2Panel,
  FiltersV2Preview,
  FiltersV2TermSearch
} from "PFApp/components/filters_v2";
import { HeaderToolbox } from "PFApp/components/header_toolbox";
import { ImportUsersModal } from "PFApp/components/import_users_modal";
import { PageEntriesCounter } from "PFApp/components/page_entries_counter";
import { SearchEntity } from "PFApp/components/search_autocomplete/search_autocomplete.types";
import SortBy from "PFApp/components/sort_by";
import { DEFAULT_ENDPOINT_LIMIT } from "PFApp/constants/api_limits";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { MultiToggle } from "PFComponents/multi_toggle";
import { Typography } from "PFComponents/typography";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { PermissionRule } from "PFTypes";
import { RecentSearchesPageKey } from "PFTypes/recent_searches";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useProfilesPageContext } from "../../context";
import { ViewMode } from "../../profiles_page.types";
import { BulkSelectOverlay } from "./bulk_select_overlay";
import css from "./profiles_header.module.scss";
import { ProfilesTableProperties } from "./profiles_table_properties";

export const ProfilesHeader = () => {
  const { t } = useTranslation("profiles");
  const { data: currentProfile } = useCurrentProfile();
  const { profiles, meta, setOrder, viewMode, setViewMode, selectedIds } = useProfilesPageContext();

  const isPermittedTo = useIsPermittedTo(currentProfile);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const isAnyProfile = profiles.length > 0;

  return (
    <Stack className={css.headerContainer} gap="spacingLg">
      {keys(selectedIds).length > 0 && <BulkSelectOverlay />}
      <Inline spread alignItems="center">
        <Typography variant="h1">{t("list.profiles")}</Typography>
        <HeaderToolbox
          items={compact([
            isPermittedTo(PermissionRule.UsersImport) && (
              <Button
                text={t("importProfiles.buttonLabel")}
                kind="secondary"
                onClick={() => setIsImportModalOpen(true)}
              />
            )
          ])}
        />
      </Inline>
      <Inline spread alignItems="center">
        <Inline alignItems="center">
          <FiltersV2TermSearch
            pageKey={RecentSearchesPageKey.Profiles}
            autocompleteConfig={[
              {
                entity: SearchEntity.Profile,
                redirect: true
              }
            ]}
          />
          {meta.order && (
            <SortBy
              sort={meta.order.value?.text}
              options={meta.order.options.map((option) => ({
                id: option.text,
                displayElement: option.text,
                item: option
              }))}
              onSortChange={setOrder}
              noLabel
            />
          )}
          {isAnyProfile && (
            <PageEntriesCounter
              count={meta.counters?.filteredProfilesCount ?? 0}
              limitThreshold={meta.counters?.endpoint_limit || DEFAULT_ENDPOINT_LIMIT}
              label={t("list.profilesCount", {
                count: meta.counters?.filteredProfilesCount
              })}
              labelWithLimit={t("list.profilesCountWithLimit", {
                count: meta.counters?.filteredProfilesCount
              })}
              limitMessage={t("list.profilesCountLimitMessage", {
                limit: (meta.counters?.endpoint_limit || DEFAULT_ENDPOINT_LIMIT).toLocaleString()
              })}
            />
          )}
        </Inline>
        <div className={css.preview}>
          <FiltersV2Preview />
        </div>
        <Inline>
          <MultiToggle
            defaultSelected={viewMode}
            options={[
              {
                id: ViewMode.Cards,
                value: <Icon name="note" size="sm" />,
                title: t("list.view.cards")
              },
              {
                id: ViewMode.Table,
                value: <Icon name="list" size="sm" />,
                title: t("list.view.table")
              }
            ]}
            onChange={setViewMode}
          />
          {viewMode === ViewMode.Table && <ProfilesTableProperties />}
          <FiltersV2Button />
          <CvExportsButton profileIds={profiles.map(({ id }) => id)} />
        </Inline>
      </Inline>
      <FiltersV2Panel />
      {isImportModalOpen && <ImportUsersModal onClose={() => setIsImportModalOpen(false)} />}
    </Stack>
  );
};
